import type { FC } from 'react';

import { useTranslation } from 'react-i18next';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { Filter } from '@vgtv/api-client/lib/component/filtered_list';
import type { CnpTagId } from '@vgtv/api-client/lib/cnp_tag';
import type { CnpStoryId } from '@vgtv/api-client/lib/cnp_story';
import cn from 'classnames';

import { List } from '../List/List';
import { Button } from '../Button/Button';
import { AssetVertical } from '../Asset/AssetVertical/AssetVertical';
import { Spinner } from '../Spinner/Spinner';
import { ListHeader } from '../ListHeader/ListHeader';
import type { ImageSources } from '../../helpers/images';
import { useAssetItemTracking } from '../PageComponents/common/useAssetItemTracking';

import { SearchForm } from './shared/SearchForm/SearchForm';

import * as styles from './FilteredList.module.scss';

export interface FilteredListAsset {
  asset: SvpAssetAdditional;
  imageSources: ImageSources;
  duration?: number;
  publishedDate?: number | null;
  title: string;
  labels?: string[];
}

export interface FilteredListProps {
  header?: string;
  filters: Filter[];
  assets: FilteredListAsset[];
  isLoading?: boolean;
  onChange: (q: {
    tagId: CnpTagId | null;
    storyId: CnpStoryId | null;
    query: string | null;
  }) => void;
  trackSearch: (query: string) => void;
  onLoadMore?: () => void;
  className: string;
  searchPlaceholder?: string;
  initiallyActiveFilter?: Filter;
  searchInputRef?: React.Ref<HTMLInputElement>;
  hideSearchField?: boolean;
  pageComponentIndex?: number;
}

const FilteredList: FC<FilteredListProps> = ({
  header,
  filters = [],
  assets = [],
  isLoading,
  onChange,
  trackSearch,
  onLoadMore,
  className,
  searchPlaceholder,
  initiallyActiveFilter,
  searchInputRef,
  hideSearchField = false,
  pageComponentIndex,
}) => {
  const { t } = useTranslation();

  const { trackAssetClick } = useAssetItemTracking({
    elementType: 'FilteredList',
    header,
    pageComponentIndex,
  });

  return (
    <div className={cn(styles.component, className)}>
      <ListHeader className={styles.header} headerContent={header} />
      <SearchForm
        filters={filters}
        onChange={onChange}
        trackSearch={trackSearch}
        placeholder={searchPlaceholder}
        initiallyActiveFilter={initiallyActiveFilter}
        searchInputRef={searchInputRef}
        hideSearchField={hideSearchField}
      />
      {isLoading && (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      )}
      {!isLoading && assets.length > 0 && (
        <div className={styles.assetList}>
          <List>
            {assets.map(({ asset, ...props }, index) => (
              <AssetVertical
                key={asset.id}
                id={asset.id}
                {...props}
                onClick={() => trackAssetClick(asset, index)}
              />
            ))}
          </List>
        </div>
      )}
      {onLoadMore !== undefined && (
        <div className={styles.seeMoreWrapper}>
          <Button
            onClick={onLoadMore}
            rounded
            size="medium"
            variant="contained"
          >
            {t('show more')}
          </Button>
        </div>
      )}
    </div>
  );
};

export { FilteredList };
